import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject
} from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef
} from "@angular/material/dialog";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import { Ticket_DetailProjection } from "@app/models/rest/ticket";
import { Router } from "@angular/router";
import moment from "moment";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from "@angular/forms";
import { TicketStreamingStore } from "@app/services/streaming/ticket-streaming-store";
import { TicketStatus } from "@app/models/enums/ticket-status.enum";
import { Visit } from "@app/models/rest/visit";
import { VisitStore } from "@app/services/store/visit-store";
import { ClientConfigService } from "@app/core/config/client-config.service";
import { select, Store } from "@ngrx/store";
import { selectorAuth } from "@app/core";
import { ResourceModel } from "@app/models/rest/resource";
import { Observable, Subject } from "rxjs";
import { VisitType } from "@app/models/rest/visit-type";
import { VisitTypeRestService } from "@app/services/hal/visit-type-rest.service";
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger
} from "@angular/material/autocomplete";
import { debounceTime, map, startWith, takeUntil } from "rxjs/operators";
import { COMMA, ENTER } from "@angular/cdk/keycodes";
import { ResourceCollection } from "@lagoshny/ngx-hateoas-client";
import {
  MatChipGrid,
  MatChipInput,
  MatChipInputEvent,
  MatChipRemove,
  MatChipRow
} from "@angular/material/chips";
import {
  ConfirmDialogComponent,
  ConfirmDialogResult
} from "@app/view/shared/confirm-dialog/confirm-dialog.component";
import { Meeting_BasicProjection } from "@app/models/rest/meeting";
import { ResourceRestService } from "@app/services/hal/resource-rest.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MeetingService } from "@app/services/api/v1/meeting/meeting.service";
import { ResourceAvailabilityResponse } from "@app/services/api/v1/meeting/dto/resource-availability-response";
import { ResourceAvailabilityRequest } from "@app/services/api/v1/meeting/dto/resource-availability-request";
import { MeetingInstanceUpdateRequest } from "@app/services/api/v1/meeting/dto/meeting-instance-update-request";
import { MeetingInstanceUpdateResponse } from "@app/services/api/v1/meeting/dto/meeting-instance-update-response";
import { ResourceConflict } from "@app/services/api/v1/meeting/dto/resource-conflict";
import { MatToolbar } from "@angular/material/toolbar";
import { MatButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import {
  MatFormField,
  MatLabel,
  MatSuffix
} from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { MatSelect } from "@angular/material/select";
import { MatOption } from "@angular/material/core";
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerToggle
} from "@angular/material/datepicker";
import { AsyncPipe, NgClass } from "@angular/common";
import { MatTooltip } from "@angular/material/tooltip";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { CdkDrag } from "@angular/cdk/drag-drop";
import { DialogHeaderComponent } from "@app/view/shared/dialog-header/dialog-header.component";

@Component({
  selector: "app-meeting-action-dialog",
  templateUrl: "./meeting-action-dialog.component.html",
  styleUrls: ["./meeting-action-dialog.component.scss"],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatIcon,
    MatDialogContent,
    MatFormField,
    MatLabel,
    MatInput,
    MatSelect,
    MatOption,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatChipGrid,
    MatChipRow,
    NgClass,
    MatTooltip,
    MatProgressSpinner,
    MatChipRemove,
    MatChipInput,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatDialogActions,
    MatButton,
    AsyncPipe,
    CdkDrag,
    DialogHeaderComponent
  ]
})
export class MeetingActionDialogComponent implements OnInit, OnDestroy {
  dialogRef = inject<MatDialogRef<MeetingActionDialogComponent>>(MatDialogRef);
  ticketStore = inject(TicketStreamingStore);
  visitStore = inject(VisitStore);
  resourceService = inject(ResourceRestService);
  protected dialog = inject(MatDialog);
  protected formBuilder = inject(FormBuilder);
  router = inject(Router);
  private meetingService = inject(MeetingService);
  serviceErrorHandler = inject(ServiceErrorHandlerService);
  clientConfigService = inject(ClientConfigService);
  visitTypeService = inject(VisitTypeRestService);
  snackBar = inject(MatSnackBar);
  protected store = inject<Store<any>>(Store);
  data = inject<{
    ticketId: number;
  }>(MAT_DIALOG_DATA);

  loading: boolean = false;
  saving: boolean = false;
  ticket: Ticket_DetailProjection;
  visit: Visit;
  meeting: Meeting_BasicProjection;
  plannerNoteForm: FormGroup;
  meetingForm: FormGroup;
  visitTypes: VisitType[] = [];
  minDate: Date = new Date();
  selectedResources: {
    resource: ResourceModel;
    status: string;
    conflicts: string[];
    loading: boolean;
    ticket: Ticket_DetailProjection;
  }[] = [];
  allResources: ResourceModel[] = [];
  filteredResources: Observable<ResourceModel[]>;
  separatorKeysCodes: number[] = [ENTER, COMMA];
  applyToFuture: boolean = false;
  private unsubscribe$ = new Subject<void>();

  @ViewChild("resourceInput") resourceInput: ElementRef<HTMLInputElement>;

  isDespatched: boolean = false;
  isEnRoute: boolean = false;
  isArrived: boolean = false;
  isStarted: boolean = false;
  isCompleted: boolean = false;
  priorityBackground: string = "";
  priorityForeground: string = "";
  public allowAudit: boolean = false;
  constructor() {
    const router = this.router;

    router.events.subscribe(() => {
      this.dialogRef.close();
    });

    this.plannerNoteForm = this.formBuilder.group({
      noteContent: [null, [Validators.required]]
    });

    this.store.pipe(select(selectorAuth)).subscribe((auth) => {
      this.allowAudit = !!auth && auth.allowAudit;
    });
  }

  ngOnInit() {
    this.loading = true;
    // Initialize the meeting form
    this.meetingForm = this.formBuilder.group({
      description: ["", Validators.required],
      visitTypeId: ["", Validators.required],
      startDate: [null, Validators.required],
      startTime: ["", Validators.required],
      duration: [30, [Validators.required, Validators.min(1)]],
      resourceCtrl: [""]
    });

    // Load visit types
    this.loadVisitTypes();

    // Load resources
    this.loadResources();

    // Setup filtered resources for autocomplete
    this.filteredResources = this.meetingForm
      .get("resourceCtrl")
      .valueChanges.pipe(
        startWith(""),
        map((value: string | null) =>
          value ? this._filterResources(value) : this._filterResources("")
        )
      );

    // Subscribe to changes in startTime and duration
    this.meetingForm
      .get("startTime")
      .valueChanges.pipe(debounceTime(300), takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.checkAllResourcesAvailability();
      });

    this.meetingForm
      .get("duration")
      .valueChanges.pipe(debounceTime(300), takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.checkAllResourcesAvailability();
      });

    // Subscribe to changes in startDate
    this.meetingForm
      .get("startDate")
      .valueChanges.pipe(debounceTime(300), takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.checkAllResourcesAvailability();
      });

    // Load ticket and related data

    this.ticketStore
      .loadSingleProjection<Ticket_DetailProjection>(
        Ticket_DetailProjection,
        this.data.ticketId,
        "detail"
      )
      .then((ticket) => {
        this.ticket = ticket;
        this.visit = ticket.visit;
        this.refreshTicketCalculations();

        // Load the meeting data from the visit
        this.visit
          .getRelation<Meeting_BasicProjection>("meeting")
          .subscribe((meeting) => {
            this.meeting = meeting;
            // Populate the form after we have the meeting data
            this.populateForm();
          });

        // Check if the visit is editable based on status
        const allowedStatuses = [
          "NEW",
          "FLOATING",
          "BOOKED",
          "REFERRED",
          "AUTO"
        ];
        if (!allowedStatuses.includes(this.visit.visitStatus)) {
          // Disable all form controls if not allowed to edit
          this.meetingForm.disable();
          // Optionally display a message to the user that this meeting cannot be edited
        }

        this.loading = false;

        // Load the selected resources after confirming the visit is editable
        this.loadSelectedResources();
      });

    // Subscribe to ticket updates
    this.ticketStore.updateStream?.subscribe((value) => {
      if (value && value.id === this.ticket.id) {
        this.refreshTicketCalculations();
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  // Load visit types for the dropdown
  loadVisitTypes() {
    this.visitTypeService.getPage().subscribe((visitTypes) => {
      this.visitTypes = visitTypes.resources.filter((x) => x.meeting);
      if (this.visitTypes.length === 1) {
        this.meetingForm.get("visitTypeId").setValue(this.visitTypes[0].id);
      }
    });
  }

  // Load all resources for the autocomplete
  loadResources() {
    this.resourceService.getPage().subscribe((resources) => {
      this.allResources = resources.resources;
      // Update the filtered resources after loading
      this.filteredResources = this.meetingForm
        .get("resourceCtrl")
        .valueChanges.pipe(
          startWith(""),
          map((value: string | null) =>
            value ? this._filterResources(value) : this._filterResources("")
          )
        );
    });
  }

  // Populate the form with existing data
  populateForm() {
    if (this.meeting) {
      this.meetingForm.get("description").setValue(this.meeting.description);
    }

    if (this.visit) {
      // Load visit type relation and set it
      this.visit.getRelation<VisitType>("visitType").subscribe((visitType) => {
        if (visitType) {
          this.meetingForm.get("visitTypeId").setValue(visitType.id);
        }
      });

      const appointmentStart = moment(this.visit.appointmentStart);
      this.meetingForm.get("startDate").setValue(appointmentStart.toDate());
      this.meetingForm
        .get("startTime")
        .setValue(appointmentStart.format("HH:mm"));
      const duration = moment(this.visit.appointmentEnd).diff(
        appointmentStart,
        "minutes"
      );
      this.meetingForm.get("duration").setValue(duration);
    }
  }

  // Load selected resources from tickets
  loadSelectedResources() {
    this.visit
      .getRelatedCollection<ResourceCollection<Ticket_DetailProjection>>(
        "ticketList",
        { params: { projection: "detail" } }
      )
      .subscribe((ticketCollection) => {
        const tickets = ticketCollection.resources;
        tickets.forEach((ticket) => {
          // Get resource
          ticket
            .getRelation<ResourceModel>("lockToResource")
            .subscribe((resource) => {
              const resourceEntry = {
                resource: resource,
                status: "Available",
                conflicts: [],
                loading: false,
                ticket: ticket
              };
              this.selectedResources.push(resourceEntry);
              // Recheck availability
              this.checkResourceAvailability(resourceEntry);
            });
        });
      });
  }

  private _filterResources(
    value: string | ResourceModel | null
  ): ResourceModel[] {
    let filterValue = "";

    if (typeof value === "string") {
      filterValue = value.toLowerCase();
    } else if (value && value.name) {
      filterValue = value.name.toLowerCase();
    } else {
      filterValue = "";
    }

    // Exclude already selected resources
    const excludedResourceIds = this.selectedResources.map(
      (sr) => sr.resource.id
    );

    return this.allResources.filter(
      (resource) =>
        resource.name.toLowerCase().includes(filterValue) &&
        !excludedResourceIds.includes(resource.id)
    );
  }

  addResource(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;

    // Add resource
    if ((value || "").trim()) {
      const resource = this.allResources.find(
        (r) => r.name.toLowerCase() === value.trim().toLowerCase()
      );
      if (
        resource &&
        !this.selectedResources.some((sr) => sr.resource.id === resource.id)
      ) {
        const resourceEntry = {
          resource: resource,
          status: "Checking",
          conflicts: [],
          loading: true,
          ticket: null
        };
        this.selectedResources.push(resourceEntry);
        this.checkResourceAvailability(resourceEntry);
      }
    }

    // Reset the input value
    if (input) {
      input.value = "";
    }

    this.meetingForm.get("resourceCtrl").setValue(null);
  }

  removeResource(resourceEntry): void {
    const index = this.selectedResources.indexOf(resourceEntry);

    if (index >= 0) {
      this.selectedResources.splice(index, 1);
    }

    // Update the filtered resources after removal
    this.meetingForm
      .get("resourceCtrl")
      .setValue(this.meetingForm.get("resourceCtrl").value);
  }

  selectedResource(event: MatAutocompleteSelectedEvent): void {
    const resource = event.option.value as ResourceModel;
    if (
      resource &&
      !this.selectedResources.some((sr) => sr.resource.id === resource.id)
    ) {
      const resourceEntry = {
        resource: resource,
        status: "Checking",
        conflicts: [],
        loading: true,
        ticket: null
      };
      this.selectedResources.push(resourceEntry);
      this.checkResourceAvailability(resourceEntry);
    }
    // Reset the input field
    this.resourceInput.nativeElement.value = "";
    this.meetingForm.get("resourceCtrl").setValue(null);
  }

  checkResourceAvailability(resourceEntry) {
    const formValue = this.meetingForm.value;

    // Parse the start date and time
    const startDateTime = moment(formValue.startDate).set({
      hour: parseInt(formValue.startTime.split(":")[0], 10),
      minute: parseInt(formValue.startTime.split(":")[1], 10),
      second: 0,
      millisecond: 0
    });
    const duration = formValue.duration;
    const endDateTime = startDateTime.clone().add(duration, "minutes");

    // Prepare the pattern entry
    const patternEntry = {
      offset: 0, // Since it's a single instance
      startHour: startDateTime.hour(),
      startMinute: startDateTime.minute(),
      duration: duration
    };

    // Prepare the request object
    const request: ResourceAvailabilityRequest = {
      resourceId: resourceEntry.resource.id,
      currentMeetingId: this.meeting.id,
      pattern: [patternEntry],
      startDate: startDateTime.toISOString(),
      endDate: endDateTime.toISOString(),
      originDate: startDateTime.toISOString(),
      length: 1 // Since we're checking a single instance
    };

    resourceEntry.loading = true;
    resourceEntry.status = "Checking";
    resourceEntry.conflicts = [];

    // Call the meetingService to check availability
    this.meetingService.checkAvailability(request).then(
      (response: ResourceAvailabilityResponse) => {
        const resourceConflict = response.resourceConflict;
        if (resourceConflict.available) {
          resourceEntry.status = "Available";
          resourceEntry.conflicts = [];
        } else {
          resourceEntry.status = "Conflicts";
          resourceEntry.conflicts = resourceConflict.conflicts;
        }
        resourceEntry.loading = false;
      },
      (error) => {
        console.error("Error checking resource availability:", error);
        resourceEntry.status = "Error";
        resourceEntry.loading = false;
      }
    );
  }

  checkAllResourcesAvailability() {
    this.selectedResources.forEach((resourceEntry) => {
      this.checkResourceAvailability(resourceEntry);
    });
  }

  promptApplyToFuture() {
    const dialogRef = this.dialog.open<
      ConfirmDialogComponent,
      unknown,
      ConfirmDialogResult
    >(ConfirmDialogComponent, {
      width: "400px",
      data: {
        message:
          "Do you want to apply changes to all future instances of this meeting?",
        buttonMode: "yes-no-cancel"
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.applyToFuture = true;
        this.submitUpdates();
      } else if (result === "no") {
        this.applyToFuture = false;
        this.submitUpdates();
      }
    });
  }

  onSubmit() {
    if (this.meetingForm.valid) {
      // Prompt to apply changes to future instances
      this.promptApplyToFuture();
    }
  }

  submitUpdates() {
    const formValue = this.meetingForm.value;
    const startDateTime = moment(formValue.startDate)
      .set({
        hour: parseInt(formValue.startTime.split(":")[0], 10),
        minute: parseInt(formValue.startTime.split(":")[1], 10),
        second: 0,
        millisecond: 0
      })
      .toISOString();
    const duration = formValue.duration;
    const endDateTime = moment(startDateTime)
      .add(duration, "minutes")
      .toISOString();

    // Prepare the update request
    const updateRequest: MeetingInstanceUpdateRequest = {
      visitId: this.visit.id,
      description: formValue.description,
      visitTypeId: formValue.visitTypeId,
      appointmentStart: startDateTime,
      appointmentEnd: endDateTime,
      resourceIds: this.selectedResources.map((entry) => entry.resource.id),
      applyToFuture: this.applyToFuture
    };

    this.saving = true;

    // Call the updateInstance method
    this.meetingService.updateInstance(updateRequest).then(
      (response: MeetingInstanceUpdateResponse) => {
        this.saving = false;
        if (response.success) {
          if (response.conflicts && response.conflicts.length > 0) {
            // Handle conflicts
            this.snackBar.open(
              "Update completed with conflicts. Please review.",
              "Close",
              { duration: 5000 }
            );
            // Display conflicts to the user
            // You can implement a method to show conflicts in the UI
            this.handleConflicts(response.conflicts);
          } else {
            this.snackBar.open("Meeting updated successfully!", "Close", {
              duration: 3000
            });
          }
          this.dialogRef.close();
        } else {
          this.snackBar.open(
            "Failed to update the meeting instance.",
            "Close",
            { duration: 5000 }
          );
        }
      },
      (error) => {
        this.saving = false;
        console.error("Error updating meeting instance:", error);
        this.snackBar.open(
          "An error occurred while updating the meeting.",
          "Close",
          { duration: 5000 }
        );
      }
    );
  }

  handleConflicts(conflicts: ResourceConflict[]) {
    // Implement logic to display conflicts to the user
    // For example, show a dialog with the conflict details
    const conflictMessages = conflicts.map(
      (conflict) =>
        `Resource ID ${conflict.resourceId}: ${conflict.conflicts.join(", ")}`
    );
    this.dialog.open(ConfirmDialogComponent, {
      width: "400px",
      data: {
        message: `Conflicts detected:\n${conflictMessages.join("\n")}`,
        showCancelButton: false
      }
    });
  }

  refreshTicketCalculations() {
    if (this.ticket.ticketStatus === TicketStatus.COMPLETE) {
      this.isCompleted = true;
      this.isStarted = true;
      this.isArrived = true;
      this.isEnRoute = true;
      this.isDespatched = true;
    } else if (this.ticket.ticketStatus === TicketStatus.STARTED) {
      this.isStarted = true;
      this.isArrived = true;
      this.isEnRoute = true;
      this.isDespatched = true;
    } else if (this.ticket.ticketStatus === TicketStatus.ARRIVED) {
      this.isArrived = true;
      this.isEnRoute = true;
      this.isDespatched = true;
    } else if (this.ticket.ticketStatus === TicketStatus.ENROUTE) {
      this.isEnRoute = true;
      this.isDespatched = true;
    } else if (this.ticket.ticketStatus === TicketStatus.DESPATCHED) {
      this.isDespatched = true;
    }
  }

  onCancelClick() {
    this.dialogRef.close();
  }
}
